import { useCallback, useState } from 'react';
import { ItemToReturn } from '@src/models/returns';
import { IPolicyValidatorResponse } from '@src/models/api';
import {
  setSelectedItems,
  setUpdatedItem,
  setReturnShipments,
  setUpsGrouping,
  setReturnMethodsResponse,
  setRosTransactionId,
  setPolicyValidatorResponse,
  useReturnStore,
  updateMaxBoxesShipments,
  deleteItemFromShipment,
} from '@src/stores/returnStore';
import {
  ChosenBoxCounts,
  collectChosenBoxCounts,
} from '@src/utils/upsGroupingUtil';
import { PolicyValidatorErrors, checkValidatorFraudType } from '@src/utils';
import { redirectToOrderDetails } from '@src/utils/general';
import { isClientSideOnlyMethod } from '@src/constants/general';
import { ReturnOptionsRefreshOutcome } from '@src/hooks/useReturnOptionsRefresh';

interface UseItemEditRemoveProps {
  selectedItems: ItemToReturn[];
  processPolicyValidator: (
    items: ItemToReturn[],
  ) => Promise<IPolicyValidatorResponse | undefined>;
  setPolicyValidatorAlert: (alert: PolicyValidatorErrors) => void;
  proceedToNextStep: (modifiedItems?: ItemToReturn[]) => void;
  orderDetailsPath: string;
  isMilitaryExchangeGuest: boolean;
  /**
   * Re-issues the token and refreshes eligibility for the remaining selection.
   * Runs before the validator, so the legs are rebuilt against the answer the
   * customer's change was actually approved under.
   */
  refreshReturnMethods: () => Promise<ReturnOptionsRefreshOutcome>;
  /**
   * Re-groups the parcel items and re-seeds their box floors. Runs after the
   * validator, because the legs it writes on to are the ones the validator has
   * just rebuilt.
   */
  refreshGrouping: (
    chosenCounts: ChosenBoxCounts,
  ) => Promise<ReturnOptionsRefreshOutcome>;
}

export const useItemEditRemove = ({
  selectedItems,
  processPolicyValidator,
  setPolicyValidatorAlert,
  proceedToNextStep,
  orderDetailsPath,
  isMilitaryExchangeGuest,
  refreshReturnMethods,
  refreshGrouping,
}: UseItemEditRemoveProps) => {
  const [renderRemoveItemModal, setRenderRemoveItemModal] = useState(false);
  const [itemIdToRemove, setItemIdToRemove] = useState<string>('');

  /**
   * Runs the calls that have to agree about a change before it can stand.
   *
   * Takes no item list on purpose. Every call here reads the selection from the
   * store, because the store is the only copy that stays current across them —
   * the callers write their change before calling, and the methods answer is
   * folded into the items part-way through. A list passed in would be the one
   * true thing at the first call and quietly stale by the third.
   */
  const handlePolicyValidationWithFraud = useCallback(
    async (
      onSuccess: () => void,
      onFraudWarned: () => void,
      isInStoreChange: boolean,
    ) => {
      setPolicyValidatorAlert(PolicyValidatorErrors.NONE);
      if (isInStoreChange) {
        onSuccess();
        return true;
      }

      // Everything that describes the return as it stands: the selection
      // itself, which items travel together, their box floors, the grouping
      // those floors came from, the service's answer about what each item is
      // eligible for, the id the fraud check ran under, and the validator's
      // answer. Taken before anything below rewrites it.
      //
      // The selection is in the set even though both callers also revert the
      // item they changed, because the two are not the same job: the methods
      // answer is folded into *every* item, so an edit reverted one item at a
      // time leaves the others carrying eligibilities derived from an answer
      // that has just been rolled back — and an item whose method that answer
      // withdrew would keep no method at all, which the validator refuses on
      // every subsequent attempt.
      //
      // The validator's answer is in the set because it is the one the
      // submitted token is currently taken from: getCurrentReturnET prefers it
      // over the methods token. Left behind, a removal undone after the
      // validator had answered would submit the restored items under a token
      // that sealed the shorter list, and create rejects items outside it.
      //
      // Restoring all of it on failure is not belt and braces — each call
      // commits its own writes as it succeeds, so a failure part-way leaves the
      // earlier ones standing and describing a change that is about to be
      // reverted. Reverting the customer's item alone leaves the review screen
      // showing their old selection against a description of the new one: a box
      // floor gone, so they may pick fewer boxes than the service accepts; no
      // suite for the create request to key their count to; and — the one that
      // reaches the customer as a rejected return rather than a wrong number —
      // an eligibility answer that no longer mentions an item they can still
      // see, which the create request would then submit with a blank return-item
      // id under a token minted without it.
      //
      // Read through getState() rather than a render closure: the drawer writes
      // the customer's change during the same tick that leads here.
      const {
        selectedItems: selectionBeforeChange,
        returnShipments: legsBeforeChange,
        upsGrouping: groupingBeforeChange,
        returnMethodsResponse: methodsBeforeChange,
        rosTransactionId: fraudReferenceBeforeChange,
        policyValidatorResponse: validatorAnswerBeforeChange,
      } = useReturnStore.getState();
      const restoreDescribedReturn = () => {
        setSelectedItems(selectionBeforeChange);
        if (legsBeforeChange) setReturnShipments(legsBeforeChange);
        setUpsGrouping(groupingBeforeChange);
        setReturnMethodsResponse(methodsBeforeChange);
        setRosTransactionId(fraudReferenceBeforeChange);
        setPolicyValidatorResponse(validatorAnswerBeforeChange);
      };

      // Read off the legs while the counts and the suites naming them are still
      // on the same object. The rebuild below clears the suites, so this cannot
      // be recovered later — and it is held here, in the scope of one
      // submission, rather than on the hook, so two submissions could never
      // spend each other's counts.
      const chosenCounts = collectChosenBoxCounts(legsBeforeChange);

      // First, because everything after it depends on the answer: it re-issues
      // the token the return will be submitted under and refreshes what each
      // remaining item is eligible for, and the legs rebuilt below are composed
      // from that answer.
      const refreshed = await refreshReturnMethods();
      if (refreshed !== 'ok') {
        // Only a genuine failure is shown as one. A `blocked` outcome means the
        // customer was answered rather than failed, and telling them the
        // service broke would be untrue — their change is still reverted.
        if (refreshed === 'failed') {
          setPolicyValidatorAlert(PolicyValidatorErrors.NETWORK);
        }
        restoreDescribedReturn();
        return false;
      }

      // Given the selection as it stands after the refresh rather than the list
      // captured before it. Folding in the methods answer can withdraw a method
      // an item no longer qualifies for, and the validator would otherwise
      // compose a leg for a method the service has just stopped offering. This
      // is the same reason the grouping call reads the store, and the two should
      // not disagree about which list is authoritative.
      const policyResponse = await processPolicyValidator(
        useReturnStore.getState().selectedItems,
      );

      if (!policyResponse) {
        // Validation failed - return false so caller restores state
        restoreDescribedReturn();
        return false;
      }

      const verifyDecision = policyResponse?.returnService?.multiVerifyDecision;
      const fraudType = checkValidatorFraudType(verifyDecision);

      if (fraudType === 'DENIED') {
        // Return false so the caller restores state. Checked before the
        // grouping call rather than after: the customer cannot proceed, so
        // there is nothing for a fresh grouping to describe.
        //
        // This branch is unreachable under the migration switch, and the old
        // note here claiming a modal is shown automatically is not true there:
        // checkValidatorFraudType returns undefined whenever the switch is on,
        // and the validator's prompt is not raised either. Which means a
        // declined customer on this screen is currently told nothing at all —
        // the prompt for the re-issued decision is MYACCR-2836's, and this
        // branch is what it will come back to.
        restoreDescribedReturn();
        return false;
      }

      // Both continuations below re-derive the box ceiling from the legs, and
      // this is what rebuilds them — so it has to land first. The floors and
      // the suite ids it writes are the two things the create request keys the
      // customer's box counts to.
      const grouped = await refreshGrouping(chosenCounts);
      if (grouped !== 'ok') {
        if (grouped === 'failed') {
          setPolicyValidatorAlert(PolicyValidatorErrors.NETWORK);
        }
        restoreDescribedReturn();
        return false;
      }

      if (!fraudType) {
        // No fraud - success path
        onSuccess();
        return true;
      }

      // WARNED - update state and show modal, user can continue
      if (onFraudWarned) {
        onFraudWarned();
      }
      return true;
    },
    [
      processPolicyValidator,
      setPolicyValidatorAlert,
      refreshReturnMethods,
      refreshGrouping,
    ],
  );

  const onEditItemSubmit = useCallback(
    async (startingState: ItemToReturn, closeDrawer: () => void) => {
      const isInStoreChange = isClientSideOnlyMethod(
        startingState.returnMethod,
      );
      const wasHandled = await handlePolicyValidationWithFraud(
        () => {
          // Success: close drawer, recalculate, and proceed
          updateMaxBoxesShipments(selectedItems);
          closeDrawer();
          proceedToNextStep();
        },
        () => {
          // WARNED: recalculate, close the drawer, modal handles continuation
          updateMaxBoxesShipments(selectedItems);
          closeDrawer();
        },
        isInStoreChange,
      );

      // Restore state if validation failed or DENIED
      if (!wasHandled) {
        setUpdatedItem(startingState);
        closeDrawer();
      }
    },
    [selectedItems, handlePolicyValidationWithFraud, proceedToNextStep],
  );

  const onRemoveItemSubmit = useCallback(
    (selectedItemId: string) => {
      setItemIdToRemove(selectedItemId);
      setRenderRemoveItemModal(true);
    },
    [setItemIdToRemove, setRenderRemoveItemModal],
  );

  const onConfirmRemoveItem = useCallback(async () => {
    const originalSelectedItems = [...selectedItems];

    const modifiedSelectedItems = selectedItems.filter(
      (item) => item.lineItemId !== itemIdToRemove,
    );
    if (modifiedSelectedItems.length === 0) {
      redirectToOrderDetails(orderDetailsPath, isMilitaryExchangeGuest);
      return;
    }

    // Find the item being removed
    const removedItem = selectedItems.find(
      (item) => item.lineItemId === itemIdToRemove,
    );

    // Only need HRTV if:
    // 1. We're removing a non-client-side item (UPS/LORP/SORIS) AND
    // 2. There are still non-client-side items remaining after removal
    const isRemovingClientSideItem = isClientSideOnlyMethod(
      removedItem?.returnMethod,
    );
    const hasRemainingApiItems = modifiedSelectedItems.some(
      (item) => !isClientSideOnlyMethod(item.returnMethod),
    );
    const isInStoreChange = isRemovingClientSideItem || !hasRemainingApiItems;

    // Applied before the calls go out rather than after they come back, and
    // that ordering is now load-bearing: the calls ask about the remaining
    // selection as the store holds it, so a removal still sitting in a local
    // variable would be sealed out of the very return it is meant to have left.
    // The edit path has always worked this way — the drawer writes the change
    // before submitting it — and the revert below covers this one the same way.
    //
    // It is also why neither continuation writes the selection any more. The
    // methods answer is folded into the items in between, and re-writing this
    // list on top would discard the refreshed eligibilities that came with it.
    setSelectedItems(modifiedSelectedItems);

    const wasHandled = await handlePolicyValidationWithFraud(
      () => {
        // Success: recalculate and proceed
        updateMaxBoxesShipments(modifiedSelectedItems);
        proceedToNextStep(modifiedSelectedItems);
        if (isInStoreChange) {
          deleteItemFromShipment(itemIdToRemove);
        }
      },
      () => {
        // WARNED: recalculate, modal will let user continue
        updateMaxBoxesShipments(modifiedSelectedItems);
      },
      isInStoreChange,
    );

    // Restore the selection if validation failed or DENIED. How the return is
    // described — the legs and the grouping — is restored alongside it by the
    // handler above, which is the only thing that knows which of its calls got
    // far enough to have rewritten them.
    if (!wasHandled) {
      setSelectedItems(originalSelectedItems);
    }
    setRenderRemoveItemModal(false);
    setItemIdToRemove('');
  }, [
    selectedItems,
    handlePolicyValidationWithFraud,
    proceedToNextStep,
    orderDetailsPath,
    isMilitaryExchangeGuest,
    setItemIdToRemove,
    itemIdToRemove,
    setRenderRemoveItemModal,
  ]);

  const onCancelRemoveItem = useCallback(() => {
    setRenderRemoveItemModal(false);
    setItemIdToRemove('');
  }, [setRenderRemoveItemModal, setItemIdToRemove]);

  return {
    onEditItemSubmit,
    onRemoveItemSubmit,
    renderRemoveItemModal,
    onCancelRemoveItem,
    onConfirmRemoveItem,
  };
};
